import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '@/shared/contexts/SettingsContext';
import { LanguageSelector } from '@/shared/components/LanguageSelector';
import type { User } from 'firebase/auth';

import { Logo } from '@/shared/components/Logo';
import { SparklesIcon, PresentationIcon, LightbulbIcon, ZapIcon, MessageSquareIcon } from './icons';
import exampleQr from '@/assets/example-qr.png';

interface LandingPageProps {
   onOpenJoinModal: () => void;
   onLogout: () => void;
   user: User | null;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onOpenJoinModal, onLogout, user }) => {
   const { t, theme, toggleTheme, auroraEnabled } = useSettings();
   const [isMenuOpen, setIsMenuOpen] = useState(false);
   const isPresenterUser = Boolean(user && !user.isAnonymous);

   const preloadDashboard = () => {
      void import('@/features/dashboard/components/UserDashboard');
   };

   const scrollToSection = (id: string) => {
      const el = document.getElementById(id);
      if (el) {
         el.scrollIntoView({ behavior: 'smooth' });
         setIsMenuOpen(false);
      }
   };

   return (
      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white transition-colors duration-300 overflow-x-hidden selection:bg-indigo-500 selection:text-white font-sans">

         {/* 1. Header with Glassmorphism */}
         <header className="fixed top-0 left-0 right-0 z-50 px-4 md:px-6 py-4">
            <nav className="max-w-7xl mx-auto bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl shadow-sm px-6 py-3 flex items-center justify-between transition-colors duration-300">
               <button
                  type="button"
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  aria-label="Scroll to top"
               >
                  <Logo />
               </button>


               {/* Desktop Nav - Links */}
               <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <button onClick={() => scrollToSection('features')} className="hover:text-indigo-600 dark:hover:text-white transition-colors">{t('features')}</button>
                  <button onClick={() => scrollToSection('how-it-works')} className="hover:text-indigo-600 dark:hover:text-white transition-colors">{t('howItWorks')}</button>
               </div>

               {/* Desktop Nav - Right Side (Lang, Theme, User Action) */}
               <div className="hidden md:flex items-center gap-3">
                  {isPresenterUser && (
                     <div className="flex items-center gap-3 pl-3 border-l border-slate-200 dark:border-slate-800 ml-2">
                        <Link
                           to="/dashboard"
                           className="px-5 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                           onMouseEnter={preloadDashboard}
                        >
                           {t('myDashboard')}
                        </Link>
                     </div>
                  )}

                  <LanguageSelector />
                  <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Toggle theme">
                     {theme === 'dark' ? '☀️' : '🌙'}
                  </button>

               </div>

               {/* Mobile Menu Button */}
               <button
                  className="md:hidden p-2 text-slate-600 dark:text-slate-300"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label="Toggle menu"
               >
                  <svg aria-hidden="true" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     {isMenuOpen ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                     ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                     )}
                  </svg>
               </button>
            </nav>

            {/* Mobile Menu */}
            {isMenuOpen && (
               <div className="absolute top-20 left-4 right-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-4 flex flex-col gap-4 md:hidden animate-in slide-in-from-top-4 duration-200">
                  <button onClick={() => scrollToSection('features')} className="text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg font-medium">{t('features')}</button>
                  <button onClick={() => scrollToSection('how-it-works')} className="text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg font-medium">{t('howItWorks')}</button>
                  <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
                  {isPresenterUser ? (
                     <>
                        <Link to="/dashboard" className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl shadow-lg text-center block" onMouseEnter={preloadDashboard}>{t('goToDashboard')}</Link>
                        <button onClick={onLogout} className="w-full py-3 text-slate-600 dark:text-slate-400 font-medium">{t('signOut')}</button>
                     </>
                  ) : (
                     <>
                        <button onClick={onOpenJoinModal} className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl shadow-lg">{t('joinSession')}</button>
                        <Link to="/login" className="w-full py-3 text-slate-600 dark:text-slate-300 font-medium border border-slate-200 dark:border-slate-700 rounded-xl text-center">{t('presenterLogin')}</Link>
                     </>
                  )}
                  <div className="flex items-center justify-between gap-2">
                     <LanguageSelector />
                     <button onClick={toggleTheme} className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 rounded-xl font-medium transition-colors">
                        {theme === 'dark' ? t('lightTheme') : t('darkTheme')}
                     </button>
                  </div>
               </div>
            )}
         </header>

         {/* 2. Hero Section with Aurora Background */}
         <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-4 overflow-hidden">
            {/* Abstract Background Blobs - Using standard css animation classes added to index.html */}
            {auroraEnabled && (
               <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                  <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-500/20 rounded-full blur-[120px] animate-blob mix-blend-screen" />
                  <div className="absolute top-[20%] right-[-10%] w-[40%] h-[40%] bg-blue-500/20 rounded-full blur-[120px] animate-blob animation-delay-2000 mix-blend-screen" />
                  <div className="absolute bottom-[-10%] left-[20%] w-[40%] h-[40%] bg-pink-500/20 rounded-full blur-[120px] animate-blob animation-delay-4000 mix-blend-screen" />
               </div>
            )}

            <div className="max-w-7xl mx-auto relative z-10 flex flex-col items-center text-center">
               <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 rounded-full text-indigo-600 dark:text-indigo-300 text-xs font-bold uppercase tracking-wide mb-6 animate-fade-in">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                  {t('newPresentationMode')}
               </div>

               <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 max-w-4xl text-transparent bg-clip-text bg-gradient-to-br from-slate-900 via-indigo-800 to-purple-900 dark:from-white dark:via-indigo-100 dark:to-purple-200 animate-slide-up">
                  {t('heroTitle')}
               </h1>

               <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mb-10 leading-relaxed animate-slide-up-delay">
                  {t('heroSubtitle')}
               </p>

               <div className="flex flex-col sm:flex-row gap-4 w-full justify-center max-w-md mx-auto mb-16 animate-slide-up-delay-2">


                  {!isPresenterUser ? (
                     <>
                        <button
                           onClick={onOpenJoinModal}
                           className="px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-2xl shadow-xl hover:scale-105 transition-[transform,box-shadow,background-color] duration-200 flex items-center justify-center gap-2"
                        >
                           <span>{t('joinSession')}</span>
                           <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </button>
                        <Link
                           to="/login"
                           className="px-8 py-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-bold rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-[background-color,box-shadow] duration-200 flex items-center justify-center"
                        >
                           {t('presenterLogin')}
                        </Link>
                     </>
                  ) : (
                     <Link
                        to="/dashboard"
                        className="px-8 py-4 bg-indigo-600 text-white font-bold rounded-2xl shadow-xl shadow-indigo-500/25 hover:bg-indigo-700 hover:scale-105 transition-[transform,box-shadow,background-color] duration-200 w-full text-center inline-block"
                        onMouseEnter={preloadDashboard}
                     >
                        {t('goToDashboard')}
                     </Link>
                  )}
               </div>

               {/* Hero Image / Dashboard Preview */}
               <div className="relative w-full max-w-4xl mx-auto animate-fade-in delay-500 perspective-1000 group">
                  {/* Glow effect behind image */}
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" />

                  <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden aspect-[16/9] md:aspect-[2/1] transform transition-transform duration-700 hover:rotate-x-2">
                     <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
                        {/* Mock UI Elements */}
                        <div className="h-full flex flex-col p-6">
                           <div className="flex gap-20 mb-8 border-b border-slate-100 dark:border-slate-800 pb-4">
                              <div className="w-32 h-8 bg-slate-200 dark:bg-slate-800 rounded-lg animate-pulse" />
                              <div className="flex-1 flex justify-end gap-3">
                                 <div className="w-24 h-8 bg-indigo-100 dark:bg-slate-800 rounded-lg" />
                                 <div className="w-8 h-8 bg-purple-100 dark:bg-slate-800 rounded-full" />
                              </div>
                           </div>
                           <div className="flex gap-6 h-full">
                              <div className="w-1/4 hidden md:flex flex-col gap-4">
                                 <div className="h-12 bg-slate-100 dark:bg-slate-800 rounded-xl w-full" />
                                 <div className="h-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl w-3/4" />
                                 <div className="h-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl w-5/6" />
                              </div>
                              <div className="flex-1 grid grid-cols-2 gap-4">
                                 <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/30 rounded-2xl p-4 flex flex-col gap-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-bold text-xs">AI</div>
                                    <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full" />
                                    <div className="w-2/3 h-3 bg-slate-200 dark:bg-slate-700 rounded-full" />
                                 </div>
                                 <div className="bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 flex flex-col gap-3">
                                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">👍</div>
                                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                    <div className="w-1/2 h-3 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                 </div>
                                 <div className="bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 flex flex-col gap-3">
                                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                    <div className="w-3/4 h-3 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                 </div>
                                 <div className="bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 flex flex-col gap-3 opacity-50">
                                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-700 rounded-full" />
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* 3. Features Grid (Bento Style) */}
         <section id="features" className="py-24 bg-slate-50 dark:bg-slate-900/50">
            <div className="max-w-7xl mx-auto px-4">
               <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('superchargedByGemini')}</h2>
                  <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                     {t('aiEngineDesc')}
                  </p>
               </div>


               <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                  {/* Row 1 */}
                  {/* Feature 1: AI Grouping */}
                  <div className="md:col-span-4 bg-white dark:bg-slate-800 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 group relative overflow-hidden backdrop-blur-sm bg-opacity-80 dark:bg-opacity-80">
                     <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-[background-color,opacity] opacity-0 group-hover:opacity-100" />
                     <div className="relative z-10">
                        <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-amber-100 dark:border-amber-800/50 group-hover:scale-110 transition-transform duration-300">
                           <LightbulbIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl font-bold mb-3 text-slate-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">{t('smartSuggestion')}</h3>
                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                           {t('smartSuggestionDesc')}
                        </p>
                     </div>
                  </div>

                  {/* Feature 2: Slide Sync */}
                  <div className="md:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 group relative overflow-hidden backdrop-blur-sm bg-opacity-80 dark:bg-opacity-80">
                     <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-[background-color,opacity] opacity-0 group-hover:opacity-100" />
                     <div className="relative z-10">
                        <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-purple-100 dark:border-purple-800/50 group-hover:scale-110 transition-transform duration-300">
                           <PresentationIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">{t('slides')}</h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                           {t('slideSyncDesc')}
                        </p>
                     </div>
                  </div>

                  {/* Row 2 */}
                  {/* Feature 3: Smart Suggestions */}
                  <div className="md:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 group relative overflow-hidden backdrop-blur-sm bg-opacity-80 dark:bg-opacity-80">
                     <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-[background-color,opacity] opacity-0 group-hover:opacity-100" />
                     <div className="relative z-10">
                        <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-indigo-100 dark:border-indigo-800/50 group-hover:scale-110 transition-transform duration-300">
                           <SparklesIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{t('semanticGrouping')}</h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                           {t('semanticGroupingDesc')}
                        </p>
                     </div>
                  </div>

                  {/* Feature 4: Live Summary */}
                  <div className="md:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 group relative overflow-hidden backdrop-blur-sm bg-opacity-80 dark:bg-opacity-80">
                     <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-[background-color,opacity] opacity-0 group-hover:opacity-100" />
                     <div className="relative z-10">
                        <div className="w-12 h-12 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-cyan-100 dark:border-cyan-800/50 group-hover:scale-110 transition-transform duration-300">
                           <ZapIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">{t('liveInsights')}</h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                           {t('liveInsightsDesc')}
                        </p>
                     </div>
                  </div>

                  {/* Feature 5: Feedback Mode */}
                  <div className="md:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 group relative overflow-hidden backdrop-blur-sm bg-opacity-80 dark:bg-opacity-80">
                     <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/20 transition-[background-color,opacity] opacity-0 group-hover:opacity-100" />
                     <div className="relative z-10">
                        <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-indigo-100 dark:border-indigo-800/50 group-hover:scale-110 transition-transform duration-300">
                           <MessageSquareIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{t('feedbackMode')}</h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                           {t('feedbackModeDesc')}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* 4. How it Works (Steps) */}
         <section id="how-it-works" className="py-24 px-4 bg-white dark:bg-slate-950">
            <div className="max-w-4xl mx-auto">
               <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">{t('effortlessTitle')}</h2>

               <div className="space-y-12">
                  {/* Step 1 */}
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                     <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-2xl text-slate-400 shrink-0">1</div>
                     <div className="flex-1 text-center md:text-left">
                        <h3 className="text-xl font-bold mb-2">{t('createSession')}</h3>
                        <p className="text-slate-600 dark:text-slate-400">{t('createSessionDesc')}</p>
                     </div>
                     <div className="flex-1 bg-slate-50 dark:bg-slate-900 rounded-2xl p-6 border border-slate-100 dark:border-slate-800">
                        <div className="text-sm font-mono bg-white dark:bg-black p-3 rounded border border-slate-200 dark:border-slate-800 text-center text-slate-600 dark:text-slate-300">
                           voxpop.ifit.work/dashboard
                        </div>
                     </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                     <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-2xl text-slate-400 shrink-0 md:order-last">2</div>
                     <div className="flex-1 bg-slate-50 dark:bg-slate-900 rounded-2xl p-6 border border-slate-100 dark:border-slate-800 md:order-last">
                        <div className="flex items-center gap-2 justify-center">
                           <div className="w-24 h-24 bg-white dark:bg-slate-800 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                              <img src={exampleQr} alt="QR" width={96} height={96} className="opacity-50 w-full h-full" />
                           </div>
                        </div>
                     </div>
                     <div className="flex-1 text-center md:text-right">
                        <h3 className="text-xl font-bold mb-2">{t('audienceJoinsQR')}</h3>
                        <p className="text-slate-600 dark:text-slate-400">{t('audienceJoinsQRDesc')}</p>
                     </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                     <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-2xl text-slate-400 shrink-0">3</div>
                     <div className="flex-1 text-center md:text-left">
                        <h3 className="text-xl font-bold mb-2">{t('aiModeratesGroups')}</h3>
                        <p className="text-slate-600 dark:text-slate-400">{t('aiModeratesGroupsDesc')}</p>
                     </div>
                     <div className="flex-1 bg-slate-50 dark:bg-slate-900 rounded-2xl p-6 border border-slate-100 dark:border-slate-800">
                        <div className="flex flex-col gap-2">
                           {/* Mock "Grouped Question" Card */}
                           <div className="bg-white dark:bg-slate-800 rounded-xl p-3 shadow-sm border border-slate-100 dark:border-slate-700/50 transform scale-95 opacity-50">
                              <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-2" />
                              <div className="h-2 bg-slate-100 dark:bg-slate-700/50 rounded w-3/4" />
                           </div>
                           <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-md border border-slate-200 dark:border-slate-700 relative z-10">
                              <div className="flex justify-between items-start mb-2">
                                 <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full border border-indigo-100 dark:border-indigo-800/50">
                                    Product
                                 </div>
                                 <div className="w-2 h-2 rounded-full bg-green-500" />
                              </div>
                              <div className="h-2.5 bg-slate-800 dark:bg-slate-200 rounded w-3/4 mb-2" />
                              <div className="h-2 bg-slate-400 dark:bg-slate-500 rounded w-1/2" />
                           </div>
                           <div className="bg-white dark:bg-slate-800 rounded-xl p-3 shadow-sm border border-slate-100 dark:border-slate-700/50 transform scale-95 opacity-50">
                              <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-2/3 mb-2" />
                              <div className="h-2 bg-slate-100 dark:bg-slate-700/50 rounded w-1/2" />
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* 5. Footer CTA */}
         <section className="py-24 px-4 text-center">
            <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-[2.5rem] p-12 relative overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800">
               <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-[100px] opacity-20 dark:opacity-30 -translate-y-1/2 translate-x-1/2" />
               <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500 rounded-full blur-[80px] opacity-20 dark:opacity-30 translate-y-1/2 -translate-x-1/2" />

               <div className="relative z-10">
                  <h2 className="text-3xl md:text-5xl font-bold mb-6">{t('readyToEngage')}</h2>
                  <p className="text-slate-600 dark:text-slate-300 text-lg mb-8 max-w-xl mx-auto">
                     {t('readyToEngageDesc')}
                  </p>
                  <button
                     onClick={onOpenJoinModal}
                     className="px-10 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-2xl shadow-xl hover:scale-105 transition-[transform,box-shadow,background-color] duration-200"
                  >
                     {t('getStartedFree')}
                  </button>
                  <p className="mt-6 text-xs text-slate-600 dark:text-slate-400">{t('noCreditCardDesc')}</p>
               </div>
            </div>
         </section>

         {/* Simple Footer */}
         <footer className="py-12 border-t border-slate-200 dark:border-slate-800 text-center text-slate-600 dark:text-slate-400 text-sm bg-white dark:bg-slate-950">
            <p>{t('footerText')}</p>
         </footer>

      </div>
   );
};
